import { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import DatePicker from "react-datepicker";
import { ro } from "date-fns/locale/ro";
import { registerLocale } from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import WarningIcon from '@mui/icons-material/Warning';
import SearchIcon from "@mui/icons-material/Search";
import DeleteIcon from '@mui/icons-material/Delete';
import './CreatePurchaseOrder.css';
import axiosInstance from '../../../Config/axiosInstance';
import BrowseProduct from './BrowseProduct';
import { NumericFormat } from "react-number-format";
import Select from 'react-select';

//intrgistram localizarea pentru react-datepicker
registerLocale("ro", ro);

const CreatePurchaseOrder = () => {
    const navigate = useNavigate();
    const [suppliers, setSuppliers] = useState([]);
    const [selectedSupplierAddress, setSelectedSupplierAddress] = useState("");
    const [delivery_address, setDeliveryAddress] = useState([]);
    const [selectedAddressDelivery, setSelectedAddressDelivery] = useState("");
    const [projectCode, setProjectCode] = useState([]);
    const [subprojectCode, setSubprojectCode] = useState([]);
    const [currency, setCurrency] = useState([]);
    const [exchangeRates, setExchangeRates] = useState([]);
    const [systemCurrencyId, setSystemCurrencyId] = useState(null);
    const [product, setProduct] = useState([]);
    const [taxes, setTaxes] = useState([]);
    const [searchTerm, setSearchTerm] = useState("");
    const [filteredProducts, setFilteredProducts] = useState([]);
    const [showModal, setShowModal] = useState(false);
    const [selectedProductsList, setSelectedProductsList] = useState([]);
    const [showConfirmModal, setShowConfirmModal] = useState(false);
    const [productToDeleteIndex, setProductToDeleteIndex] = useState(null);
    const [procurementId, setProcurementId] = useState(null);
    const [errors, setErrors] = useState({});
    const [orderSummary, setOrderSummary] = useState(null);
    const [isCalculating, setIsCalculating] = useState(false);
    const calculationInProgress = useRef(false);
    const [isDraftMode, setIsDraftMode] = useState(false);
    const [arrivalDeadline, setArrivalDeadline] = useState(null);
    const [paymentTermNumber, setPaymentTermNumber] = useState("");
    const [isSubprojectCodeDisabled, setIsSubprojectCodeDisabled] = useState(true);

    //helper: converteste orice (inclusiv array [0]) intr-un numar finit; altfel 0
    const toNumber = useCallback((v) => {
        if (Array.isArray(v)) v = v[0];
        if (v === null || v === undefined || v === '') return 0;
        const n = parseFloat(v);
        return Number.isFinite(n) ? n : 0;
    }, []);

    //helper: despacheteaza array [x] -> x (pentru id-uri)
    const unwrapId = useCallback((v) => {
        return Array.isArray(v) ? v[0] : v;
    }, []);

    //folosim useMemo pentru a preveni recrearea obiectului la fiecare render
    const initialFormData = useMemo(() => ({
        project_code: "",
        subproject_code: "",
        supplier: "",
        arrival_deadline: "",
        payment_term: "",
        exchange_rate: "",
        currency: "",
        tax: "",
        note_to_supplier: "",
        status: "",
        total_wo_vat: "",
        vat_value: "",
        vat_value_tax: 0,
        total: "",
        delivery_address: "",
        delivery_conditions: "",
        tax_value: 0,
    }), []);

    const [formData, setFormData] = useState(initialFormData);
    
    //functie pentru formatarea datei
    const formatDate = useCallback((dateString) => {
        if (!dateString) return '';
        const date = new Date(dateString);
        return `${date.getDate().toString().padStart(2, '0')}.${(date.getMonth()+1).toString().padStart(2, '0')}.${date.getFullYear()}`;
    }, []);
    
    //functie pentru incarcarea optiunilor exchange rate in functie de currency
    const exchangeOptionsByCurrencyId = useMemo(() => {
        const m = new Map();
        
        for (const rate of exchangeRates) {
            const currencyId = rate.currency;
            if (!m.has(currencyId)) m.set(currencyId, []);
            
            //formateaza valoarea cu , pentru afisare
            const formattedValue = new Intl.NumberFormat('ro-RO', {
                minimumFractionDigits: 4,
                maximumFractionDigits: 4
            }).format(Number(rate.value));
            
            m.get(currencyId).push({
                value: rate.id,
                label: `${formattedValue} (${formatDate(rate.date)})`,
                shortLabel: formattedValue,
                rateValue: Number(rate.value),
                rateDate: rate.date,
                currency: currencyId
            });
        }
        
        //sorteaza fiecare grup dupa data
        for (const [currencyId, arr] of m.entries()) {
            arr.sort((a, b) => new Date(b.rateDate) - new Date(a.rateDate));
            m.set(currencyId, arr);
        }
        
        return m;
    }, [exchangeRates, formatDate]);

    //functie pentru rotunjirea la 4 zecimale pentru trimiterea datelor
    const roundToFourDecimals = useCallback((value) => {
        if (!value && value !== 0) return 0;
        const num = parseFloat(value);
        if (isNaN(num)) return 0;
        return Math.round(num * 10000) / 10000;
    }, []);

    const isFormDirty = useCallback(() => {
        return Object.keys(initialFormData).some(
            key => formData[key] !== initialFormData[key]
        ) || selectedProductsList.length > 0;
    }, [formData, selectedProductsList, initialFormData]);

    
    const supplierOptions = suppliers.map(supplier => ({
        value: supplier.id,
        label: supplier.name,
        address: supplier.address
    }));

    const deliveryAddressOptions = delivery_address.map(address => ({
        value: address.id,
        label: address.name, 
        address: address.address
    }));

    const projectCodeOptions = projectCode.map(code => ({
        value: code.id,
        label: code.code, 
    }));

    const subprojectCodeOptions = subprojectCode.map(code => ({
        value: code.id,
        label: code.code,
    }));

    //functie pentru incarcarea subproject codes in functie de project codes
    const fetchSubprojectCodesByProject = useCallback(async (projectCodeId) => {
        if(!projectCodeId) {
            setSubprojectCode([]);
            return;
        }

        try {
            const response = await axiosInstance.get(
                `/subprojectcodes/by_project/${projectCodeId}/`
            );
            setSubprojectCode(response.data);
        } catch (error) {
            console.error("Error fetching subproject codes: ", error);
            setSubprojectCode([]);
        }
    }, []);

    //functie pentru a retine rata de schimb curenta
    const getCurrentExchangeRate = useCallback(() => {
        if (!formData.exchange_rate) return null;
        const selectedRate = exchangeRates.find(rate => rate.id === parseInt(formData.exchange_rate));
        return selectedRate ? selectedRate.value : null;
    }, [formData.exchange_rate, exchangeRates]);

    //functie pentru calcularea totalurilor pe backend
    const calculateTotals = useCallback(async (items, tax_value, vat_value_tax, currencyId, exchangeRateValue) => {
        try {
            setIsCalculating(true);
            
            const response = await axiosInstance.post('/purchase-order/calculate-po/', {
                items: items,
                tax_value: roundToFourDecimals(tax_value),
                vat_value_tax: roundToFourDecimals(vat_value_tax),
                currency_id: currencyId ? parseInt(currencyId) : null,
                exchange_rate_value: exchangeRateValue
            });
            
            if (response.data) {
                if (response.data.summary) {
                    //valori in moneda originala
                    response.data.summary.total_without_vat = roundToFourDecimals(response.data.summary.total_without_vat);
                    response.data.summary.total_vat = roundToFourDecimals(response.data.summary.total_vat);
                    response.data.summary.delivery_tax = roundToFourDecimals(response.data.summary.delivery_tax);
                    response.data.summary.delivery_vat = roundToFourDecimals(response.data.summary.delivery_vat);
                    response.data.summary.total_delivery = roundToFourDecimals(response.data.summary.total_delivery);
                    response.data.summary.total = roundToFourDecimals(response.data.summary.total);
                    
                    //valori in moneda sistemului
                    response.data.summary.total_without_vat_system = roundToFourDecimals(response.data.summary.total_without_vat_system || 0);
                    response.data.summary.total_vat_system = roundToFourDecimals(response.data.summary.total_vat_system || 0);
                    response.data.summary.delivery_tax_system = roundToFourDecimals(response.data.summary.delivery_tax_system || 0);
                    response.data.summary.total_delivery_system_currency = roundToFourDecimals(response.data.summary.total_delivery_system_currency || 0);
                    response.data.summary.total_system_currency = roundToFourDecimals(response.data.summary.total_system_currency || 0);
                }
                if (response.data.items) {
                    response.data.items = response.data.items.map(item => ({
                        ...item,
                        subtotal: roundToFourDecimals(item.subtotal),
                        vat_amount: roundToFourDecimals(item.vat_amount),
                        total: roundToFourDecimals(item.total),
                        total_system_currency: roundToFourDecimals(item.total_system_currency || 0)
                    }));
                }
            }
            
            return response.data;
        } catch (error) {
            console.error('Error calculating totals:', error);
            return null;
        } finally {
            setIsCalculating(false);
        }
    }, [roundToFourDecimals]);

    //functie pentru calcularea totalului unui singur item
    const calculateItemTotals = useCallback(async (quantity, unit_price, vat_percent, currencyId, exchangeRateValue) => {
        try {
            const response = await axiosInstance.post('/purchase-order/calculate-item/', {
                quantity: roundToFourDecimals(quantity),
                unit_price: roundToFourDecimals(unit_price),
                vat_value: roundToFourDecimals(vat_percent),
                currency_id: currencyId ? parseInt(currencyId) : null,
                exchange_rate_value: exchangeRateValue
            });
            
            if (response.data) {
                response.data.subtotal = roundToFourDecimals(response.data.subtotal);
                response.data.vat_amount = roundToFourDecimals(response.data.vat_amount);
                response.data.total = roundToFourDecimals(response.data.total);
                response.data.total_system_currency = roundToFourDecimals(response.data.total_system_currency || 0);
            }
            
            return response.data;
        } catch (error) {
            console.error('Error calculating item:', error);
            return null;
        }
    }, [roundToFourDecimals]);

    //functie pentru recalcularea totalurilor comenzii
    const recalculateOrderTotals = useCallback(async () => {
        if (selectedProductsList.length === 0) {
            setOrderSummary(null);
            setFormData(prev => ({
                ...prev,
                total_wo_vat: 0,
                vat_value: 0,
                total: 0
            }));
            return;
        }

        const itemsForCalculation = selectedProductsList.map(prod => ({
            product: unwrapId(prod.id),
            quantity: roundToFourDecimals(toNumber(prod.quantity)),
            unit_price: roundToFourDecimals(toNumber(prod.cost)),
            vat_value: roundToFourDecimals(toNumber(prod.vat))
        }));
        
        const exchangeRateValue = getCurrentExchangeRate();
        const currencyId = formData.currency;
        
        try {
            const result = await calculateTotals(
                itemsForCalculation,
                roundToFourDecimals(toNumber(formData.tax_value)),
                roundToFourDecimals(toNumber(formData.vat_value_tax)),
                currencyId,
                exchangeRateValue
            );
            
            if (result) {
                setFormData(prev => ({
                    ...prev,
                    total_wo_vat: roundToFourDecimals(result.calculated_values?.total_wo_vat || result.summary.total_without_vat),
                    vat_value: roundToFourDecimals(result.calculated_values?.vat_value || result.summary.total_vat),
                    total: roundToFourDecimals(result.calculated_values?.total || result.summary.total)
                }));
                
                if (result.summary) {
                    const roundedSummary = {
                        // Valori în moneda originală
                        total_without_vat: roundToFourDecimals(result.summary.total_without_vat),
                        total_vat: roundToFourDecimals(result.summary.total_vat),
                        delivery_tax: roundToFourDecimals(result.summary.delivery_tax),
                        delivery_vat: roundToFourDecimals(result.summary.delivery_vat),
                        total_delivery: roundToFourDecimals(result.summary.total_delivery),
                        total: roundToFourDecimals(result.summary.total),
                        // Valori în moneda sistemului
                        total_without_vat_system: roundToFourDecimals(result.summary.total_without_vat_system || 0),
                        total_vat_system: roundToFourDecimals(result.summary.total_vat_system || 0),
                        delivery_tax_system: roundToFourDecimals(result.summary.delivery_tax_system || 0),
                        total_delivery_system_currency: roundToFourDecimals(result.summary.total_delivery_system_currency || 0),
                        total_system_currency: roundToFourDecimals(result.summary.total_system_currency || 0)
                    };
                    setOrderSummary(roundedSummary);
                }
            }
        } catch (error) {
            console.error('Error recalculating order totals:', error);
        }
    }, [selectedProductsList, formData.tax_value, formData.vat_value_tax, formData.currency, roundToFourDecimals, calculateTotals, getCurrentExchangeRate, toNumber, unwrapId]);

    //recalculeaza totalurile cand se schimba currency sau exchange rate
    useEffect(() => {
        if (selectedProductsList.length > 0 && !calculationInProgress.current) {
            recalculateOrderTotals();
        }
    }, [formData.currency, formData.exchange_rate, recalculateOrderTotals, selectedProductsList.length]);

    //validare campuri
    const validateForm = useCallback(() => {
        const newErrors = {};

        if(!formData.supplier) newErrors.supplier = "Supplier is required";
        if(!formData.delivery_address) newErrors.delivery_address = "Delivery address is required";
        if(!formData.project_code) newErrors.project_code = "Project code is required";
        if(!formData.delivery_conditions) newErrors.delivery_conditions = "Delivery condition is required";
        if(!formData.arrival_deadline) newErrors.arrival_deadline = "Arrival deadline is required";
        
        //validare payment_term 
        if(!paymentTermNumber || paymentTermNumber === "") {
            newErrors.payment_term = "Payment term is required";
        } else {
            const numValue = parseInt(paymentTermNumber, 10);
            if (isNaN(numValue) || numValue <= 0) {
                newErrors.payment_term = "Payment term must be a positive number";
            }
        }
        
        if(!formData.currency) newErrors.currency = "Currency is required";
        
        //verifica daca e nevoie de exchange rate
        const isSystemCurrency = systemCurrencyId === parseInt(formData.currency);
        if(!formData.exchange_rate && !isSystemCurrency && formData.currency) {
            newErrors.exchange_rate = "Exchange rate is required";
        }
        
        if(!formData.delivery_conditions) newErrors.delivery_conditions = "Delivery conditions are required";

        //validare produse
        selectedProductsList.forEach((prod, index) => {
            if (prod.quantity === '' || prod.quantity == null || toNumber(prod.quantity) <= 0) {
                newErrors[`quantity_${index}`] = true;
            }
            if (!prod.um) newErrors[`um_${index}`] = true;
            if (prod.vat === '' || prod.vat === null || prod.vat === undefined) {
                newErrors[`vat_${index}`] = true;
            }
            if (prod.cost === '' || prod.cost === null || prod.cost === undefined) {
                newErrors[`cost_${index}`] = true;
            }
            if (!prod.arrival_deadline) newErrors[`arrival_${index}`] = true;
        });

        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    }, [formData, selectedProductsList, systemCurrencyId, paymentTermNumber, toNumber]);

    const handleSelectChange = useCallback((selectOption, {name}) => {
        setFormData(prev => ({
            ...prev, 
            [name]: selectOption ? selectOption.value : ""
        }))

        if(name === "supplier") {
            setSelectedSupplierAddress(selectOption ? selectOption.address : "N/A");
        }

        if(name === "delivery_address") {   
            setSelectedAddressDelivery(selectOption ? selectOption.address : "N/A");
        }

        //cand se selecteaza un cod de proiect
        if(name === "project_code") {
            const projectCodeId = selectOption ? selectOption.value : null;
            
            if(projectCodeId) {
                //daca s a selectat un project code, incarca subproject code si activeaza selectul
                fetchSubprojectCodesByProject(projectCodeId);
                setIsSubprojectCodeDisabled(false);
            } else {
                //daca s a deselectat project code, dezactiveaza si goleste subproject code
                setSubprojectCode([]);
                setIsSubprojectCodeDisabled(true);
                setFormData(prev => ({
                    ...prev, 
                    subproject_code: "",
                }));
            }
        }

    }, [fetchSubprojectCodesByProject]);
    
    //functie pentru gestionarea datelor din DatePicker
    const handleDateChange = useCallback((date) => {
        if(!date) {
            setFormData(prev => ({
                ...prev,
                arrival_deadline: ""
            }));
            setArrivalDeadline(null);
            return;
        }

        const year = date.getFullYear();
        const month = String(date.getMonth()+1).padStart(2, "0");
        const day = String(date.getDate()).padStart(2, "0");

        const formattedDate = `${year}-${month}-${day}`;

        //salvam data veche inainte de update
        const oldHeaderDate = formData.arrival_deadline;

        //actualizam headerul
        setFormData(prev => ({
            ...prev, 
            arrival_deadline: formattedDate
        }));
    
        setArrivalDeadline(date);

        //actualizam doar liniile care aveau aceeasi data ca header ul vechi
        setSelectedProductsList(prev => 
            prev.map(prod => ({
                ...prod,
                arrival_deadline:
                    prod.arrival_deadline === oldHeaderDate
                        ? formattedDate
                        : prod.arrival_deadline
            }))
        );
    }, [formData.arrival_deadline]);

    //functie pentru gestionarea modificarii payment_term 
    const handlePaymentTermChange = useCallback((e) => {
        const value = e.target.value;

        if (value === "" || /^\d+$/.test(value)) {
            setPaymentTermNumber(value);
            if (errors.payment_term) {
                setErrors(prev => ({ ...prev, payment_term: "" }));
            }
        }
    }, [errors.payment_term]);

    useEffect(() => {
        const fetchDepartments = async () => {
            try {
                const response = await axiosInstance.get('/departments/');
                const procurementDept = response.data.find(dept => dept.name === "Procurement");
                if (procurementDept) {
                    setProcurementId(procurementDept.id);
                }
            } catch (error) {
                console.error("Error fetching departments: ", error);
            }
        };

        fetchDepartments();
    }, []);

    useEffect(() => {
        if (!procurementId) return;

        const fetchActiveSuppliers = async () => {
            try {
                const response = await axiosInstance.get(
                    `/suppliers/active-by-department/?department=${procurementId}`
                );
                setSuppliers(response.data);
            } catch (error) {
                console.error("Error fetching active suppliers: ", error);
            }
        };

        fetchActiveSuppliers();
    }, [procurementId]);

    useEffect(() => {
        const fetchAddressDelivery = async () => {
            try {
                const response = await axiosInstance.get('/delivery-address/deliveryAddress/');
                setDeliveryAddress(response.data);
            } catch (error) {
                console.error('Error fetching address delivery: ', error);
            }
        };
        fetchAddressDelivery();
    }, []);

    useEffect(() => {
        const fetchProjectCode = async () => {
            try {
                const response = await axiosInstance.get('/code_registries/');
                setProjectCode(response.data);
            } catch (error) {
                console.error('Error fetching project code: ', error);
            }
        }
        fetchProjectCode();
    }, []);

    useEffect(() => {
        const fetchSubprojectCode = async() => {
            try {
                const response = await axiosInstance.get('/subprojectcodes/');
                setSubprojectCode(response.data);
            } catch (error) {
                console.error("Error fetching subproject code: ", error);
            }
        }
        fetchSubprojectCode();
    }, []);

    useEffect(() => {
        const fetchCurrency = async () => {
            try {
                const response = await axiosInstance.get('/currencies/');
                setCurrency(response.data);
            } catch (error) {
                console.error('Error fetching currency: ', error);
            }
        }
        fetchCurrency();
    }, []);

    useEffect(() => {
        const fetchExchangeRates = async () => {
            try {
                const response = await axiosInstance.get('/exchange-rate/');
                const sortedRates = (response.data || []).sort(
                    (a, b) => new Date(b.date) - new Date(a.date)
                );
                setExchangeRates(sortedRates);
            } catch (error) {
                console.error('Error fetching exchange rate: ', error);
            }
        }
        fetchExchangeRates();
    }, []);

    useEffect(() => {
        const fetchSystemCurrency = async () => {
            try {
                const response = await axiosInstance.get('/system-currency/1/');
                setSystemCurrencyId(response.data?.currency ?? null);
            } catch (error) {
                console.error('Error fetching system currency: ', error);
            }
        }
        fetchSystemCurrency();
    }, []);

    useEffect(() => {
        const fetchProduct = async () => {
            try {
                const response = await axiosInstance.get('/product/');
                const activeProducts = response.data.filter(product => product.status === "ACTIVE");
                setProduct(activeProducts);
                setFilteredProducts(activeProducts);
            } catch (error) {
                console.error('Error fetching product: ', error);
            }
        }
        fetchProduct();
    }, []);

    useEffect(() => {
        const fetchTaxes = async () => {
            try {
                const response = await axiosInstance.get('/taxes/taxes/');
                setTaxes(response.data);
            } catch (error) {
                console.error('Error fetching taxes: ', error);
            }
        }
        fetchTaxes();
    }, []);

    useEffect(() => {
        if (selectedProductsList.length > 0 && !calculationInProgress.current) {
            recalculateOrderTotals();
        }
    }, [selectedProductsList, formData.tax_value, formData.vat_value_tax, recalculateOrderTotals]);

    useEffect(() => {
        if (!searchTerm) {
            setFilteredProducts([]);
        } else {
            const filtered = product.filter(prod =>
                prod.title &&
                prod.title.toLowerCase().includes(searchTerm.toLowerCase()) &&
                !selectedProductsList.some(p => p.id === prod.id)
            );
            setFilteredProducts(filtered);
        }
    }, [searchTerm, product, selectedProductsList]);

    useEffect(() => {
        const recalculateAllItems = async () => {
            if(selectedProductsList.length === 0) return;

            const exchangeRateValue = getCurrentExchangeRate();
            const currencyId = formData.currency;

            //previne recalculari multiple
            if(calculationInProgress.current) return;
            calculationInProgress.current = true;

            try {
                const updatedList = [...selectedProductsList];
                let hasChanges = false;

                for(let i=0; i < updatedList.length; i++) {
                    const item = updatedList[i];
                    const quantity = roundToFourDecimals(toNumber(item.quantity));
                    const cost = roundToFourDecimals(toNumber(item.cost));
                    const vat = roundToFourDecimals(toNumber(item.vat));

                    if(quantity > 0) {
                        const result = await calculateItemTotals(quantity, cost, vat, currencyId, exchangeRateValue);
                        if (result) {
                            const newSubtotal = roundToFourDecimals(result.subtotal);
                            const newVatAmount = roundToFourDecimals(result.vat_amount);
                            const newTotal = roundToFourDecimals(result.total);
                            const newTotalSystem = roundToFourDecimals(result.total_system_currency || 0);
                            
                            //verifica daca s-au schimbat valorile
                            if(item.subtotal !== newSubtotal || 
                                item.vat_amount !== newVatAmount || 
                                item.total !== newTotal ||
                                item.total_system_currency !== newTotalSystem) { 
                                    updatedList[i].subtotal = newSubtotal;
                                    updatedList[i].vat_amount = newVatAmount;
                                    updatedList[i].total = newTotal;
                                    updatedList[i].total_system_currency = newTotalSystem
                                    hasChanges = true;
                            }
                        }
                    }
                }
                if(hasChanges) {
                    setSelectedProductsList(updatedList);
                    await recalculateOrderTotals();
                }
            } catch (error) {
                console.error('Error recalculating all items: ', error);
            } finally {
                calculationInProgress.current = false;
            }
        };

        //verifica daca avem produse si datele necesare
        if(selectedProductsList.length > 0 && formData.currency) {
            recalculateAllItems();
        }
    }, [formData.currency, formData.exchange_rate, systemCurrencyId, selectedProductsList, getCurrentExchangeRate, roundToFourDecimals, calculateItemTotals, recalculateOrderTotals, toNumber]);

    const handleChange = useCallback((e) => {
        const { name, value } = e.target;
        
        if (name === "tax_value") {
            setFormData(prev => ({ ...prev, [name]: roundToFourDecimals(toNumber(value)) }));
        } else if (name === "vat_value_tax") {
            setFormData(prev => ({ ...prev, [name]: roundToFourDecimals(toNumber(value)) }));
        } else if (name === "tax") {
            const selectedTax = taxes.find(tax => String(tax.id) === String(value));
            
            if (!value) {
                setFormData(prev => ({
                    ...prev,
                    tax: "",
                    vat_value_tax: 0, 
                    tax_value: 0,
                }));
            } 
            else if (selectedTax) {
                setFormData(prev => ({
                    ...prev,
                    tax: value,
                    vat_value_tax: roundToFourDecimals(selectedTax.value ?? 21), 
                    tax_value: 0,
                }));
            }
        } else if (name !== "payment_term") { // Ignorăm payment_term aici pentru că are handler separat
            setFormData(prev => ({ ...prev, [name]: value }));
        }

        if (name === "supplier") {
            if (suppliers.length > 0) {
                const selectedSupplier = suppliers.find(supplier => String(supplier.id) === String(value));
                setSelectedSupplierAddress(selectedSupplier ? selectedSupplier.address : "N/A");
            }
        }

        if (name === "delivery_address") {
            if (delivery_address.length > 0) {
                const selectedAddressDelivery = delivery_address.find(delAdd => String(delAdd.id) === String(value));
                setSelectedAddressDelivery(selectedAddressDelivery ? selectedAddressDelivery.address : "N/A");
            }
        }
    }, [roundToFourDecimals, taxes, suppliers, delivery_address, toNumber]);

    const handleProductSelect = useCallback((prod) => {
        setSelectedProductsList(prev => [...prev, { 
            ...prod, 
            quantity: '', 
            um: '', 
            vat: '21', 
            cost: '', 
            subtotal: 0,
            vat_amount: 0,
            total: 0,
            total_system_currency: 0, 
            arrival_deadline: formData.arrival_deadline || '' 
        }]);
        setSearchTerm('');
        setFilteredProducts([]);
    }, [formData.arrival_deadline]);

    const handleProductLineChange = useCallback(async (index, field, value) => {
        const updatedList = [...selectedProductsList];
        
        if (field === "quantity" || field === "cost" || field === "vat") {
            updatedList[index][field] = roundToFourDecimals(toNumber(value));
        } else {
            updatedList[index][field] = value;
        }
        
        if (field === "quantity" || field === "cost" || field === "vat") {
            const quantity = roundToFourDecimals(toNumber(updatedList[index].quantity));
            const cost = roundToFourDecimals(toNumber(updatedList[index].cost));
            const vat = roundToFourDecimals(toNumber(updatedList[index].vat));
            
            if (quantity > 0) {
                try {
                    const exchangeRateValue = getCurrentExchangeRate();
                    const currencyId = formData.currency;
                    
                    const result = await calculateItemTotals(quantity, cost, vat, currencyId, exchangeRateValue);
                    if (result) {
                        updatedList[index].subtotal = roundToFourDecimals(result.subtotal);
                        updatedList[index].vat_amount = roundToFourDecimals(result.vat_amount);
                        updatedList[index].total = roundToFourDecimals(result.total);
                        updatedList[index].total_system_currency = roundToFourDecimals(result.total_system_currency || 0);
                    }
                } catch (error) {
                    console.error('Error calculating item:', error);
                }
            } else {
                updatedList[index].subtotal = 0;
                updatedList[index].vat_amount = 0;
                updatedList[index].total = 0;
                updatedList[index].total_system_currency = 0;
            }
            
            setSelectedProductsList(updatedList);
        } else {
            setSelectedProductsList(updatedList);
        }
    }, [selectedProductsList, roundToFourDecimals, calculateItemTotals, getCurrentExchangeRate, formData.currency, toNumber]);

    const handleDeleteProduct = useCallback((index) => {
        const updatedList = [...selectedProductsList];
        updatedList.splice(index, 1);
        setSelectedProductsList(updatedList);
    }, [selectedProductsList]);

    const handleAskDeleteProduct = useCallback((index) => {
        setProductToDeleteIndex(index);
        setShowConfirmModal(true);
    }, []);

    const confirmDeleteProduct = useCallback(() => {
        if (productToDeleteIndex !== null) {
            handleDeleteProduct(productToDeleteIndex);
            setProductToDeleteIndex(null);
            setShowConfirmModal(false);
        }
    }, [productToDeleteIndex, handleDeleteProduct]);

    const getSelectedCurrencyLabel = useCallback(() => {
        const selected = currency.find(c => String(c.id) === String(formData.currency));
        return selected ? selected.name : '';
    }, [currency, formData.currency]);

    const getSystemCurrencyLabel = useCallback(() => {
        const systemCurrency = currency.find(c => c.id === systemCurrencyId);
        return systemCurrency ? systemCurrency.name : '';
    }, [currency, systemCurrencyId]);

    const handleSaveAsDraft = useCallback(async () => {
        //seteaza modul DRAFT si sterge erorile existente
        setIsDraftMode(true);
        setErrors({});

        //verifica daca exista produse
        if (selectedProductsList.length === 0) {
            alert("Please add at least one product to the purchase order.");
            return;
        }

        //calculeaza totalurile daca e necesar
        if (!orderSummary && selectedProductsList.length > 0) {
            await recalculateOrderTotals();
        }

        //pregateste payload-ul
        const paymentTermValue = paymentTermNumber ? parseInt(paymentTermNumber, 10) : null;

        const orderPayload = {
            project_code: formData.project_code || null,
            subproject_code: formData.subproject_code || null,
            supplier: formData.supplier || null,
            delivery_address: formData.delivery_address || null,
            arrival_deadline: formData.arrival_deadline || null,
            currency: formData.currency || null,
            exchange_rate: formData.exchange_rate || null,
            tax: formData.tax || null,
            tax_value: roundToFourDecimals(toNumber(formData.tax_value)),
            note_to_supplier: formData.note_to_supplier || '',
            payment_term: paymentTermValue,
            status: "DRAFT",
            delivery_conditions: formData.delivery_conditions || '',
            total_wo_vat: roundToFourDecimals(toNumber(formData.total_wo_vat)),
            vat_value: roundToFourDecimals(toNumber(formData.vat_value)),
            total: roundToFourDecimals(toNumber(formData.total)),
            items: selectedProductsList.map(p => ({
                product: unwrapId(p.id),
                unit: p.um || '',
                quantity: roundToFourDecimals(toNumber(p.quantity)),
                unit_price: roundToFourDecimals(toNumber(p.cost)),
                tax: unwrapId(formData.tax) || null,
                note_to_supplier: formData.note_to_supplier || '',
                status: "DRAFT",
                total_wo_vat: roundToFourDecimals(toNumber(p.subtotal)),
                vat_value: roundToFourDecimals(toNumber(p.vat)),
                vat_value_tax: roundToFourDecimals(toNumber(formData.vat_value_tax)),
                total: roundToFourDecimals(toNumber(p.total)),
                total_system_currency: roundToFourDecimals(toNumber(p.total_system_currency)),
                arrival_deadline: p.arrival_deadline || null,
            }))
        };

        try {
            const response = await axiosInstance.post('/purchase-order/', orderPayload);
            console.log("Saved as draft:", response.data);
            navigate(-1);
        } catch (error) {
            console.error("Save draft failed: ", error.response?.data || error.message);
            
            if (error.response?.data) {
                const errorData = error.response.data;
                let errorMessage = "Failed to save purchase order as draft.\n";
                
                if (typeof errorData === 'object') {
                    Object.keys(errorData).forEach(key => {
                        if (Array.isArray(errorData[key])) {
                            errorMessage += `${key}: ${errorData[key].join(', ')}\n`;
                        } else {
                            errorMessage += `${key}: ${errorData[key]}\n`;
                        }
                    });
                } else {
                    errorMessage += errorData;
                }
                
                alert(errorMessage);
            } else {
                alert("Failed to save purchase order as draft. Please try again.");
            }
        }
    }, [selectedProductsList, orderSummary, recalculateOrderTotals, formData, roundToFourDecimals, navigate, paymentTermNumber, toNumber, unwrapId]);

    const handleSubmitForApproval = useCallback(async () => {
        //seteaza modul normal
        setIsDraftMode(false);

        if(!validateForm()) {
            alert("Please fill in all required fields");
            return;
        }

        if (selectedProductsList.length === 0) {
            alert("Please add at least one product to the purchase order.");
            return;
        }

        if (!orderSummary) {
            await recalculateOrderTotals();
        }

        const paymentTermValue = paymentTermNumber ? parseInt(paymentTermNumber, 10) : null;

        const orderPayload = {
            project_code: formData.project_code,
            subproject_code: formData.subproject_code || null,
            supplier: formData.supplier,
            delivery_address: formData.delivery_address,
            arrival_deadline: formData.arrival_deadline,
            currency: formData.currency,
            exchange_rate: formData.exchange_rate,
            tax: formData.tax || null,
            tax_value: roundToFourDecimals(toNumber(formData.tax_value)),
            note_to_supplier: formData.note_to_supplier,
            payment_term: paymentTermValue,
            status: "WAITING APPROVAL",
            delivery_conditions: formData.delivery_conditions,
            total_wo_vat: roundToFourDecimals(toNumber(formData.total_wo_vat)),
            vat_value: roundToFourDecimals(toNumber(formData.vat_value)),
            total: roundToFourDecimals(toNumber(formData.total)),
            items: selectedProductsList.map(p=> ({
                product: unwrapId(p.id),
                unit: p.um,
                quantity: roundToFourDecimals(toNumber(p.quantity)),
                unit_price: roundToFourDecimals(toNumber(p.cost)),
                tax: unwrapId(formData.tax) || null,
                note_to_supplier: formData.note_to_supplier,
                status: 'WAITING APPROVAL',
                total_wo_vat: roundToFourDecimals(toNumber(p.subtotal)),
                vat_value: roundToFourDecimals(toNumber(p.vat)),
                vat_value_tax: roundToFourDecimals(toNumber(formData.vat_value_tax)),
                total: roundToFourDecimals(toNumber(p.total)),
                total_system_currency: roundToFourDecimals(toNumber(p.total_system_currency)),
                arrival_deadline: p.arrival_deadline,
            }))
        };
        try {
            const response = await axiosInstance.post('/purchase-order/', orderPayload);
            const createdOrder = response.data;
            await axiosInstance.get(`/purchase-order/${createdOrder.id}/aprobare_comanda/`);    
            console.log("Submitted for approval:", createdOrder);
            navigate(-1);
        } catch (error) {
            console.error("Submit for approval failed: ", error.response?.data || error.message);
            console.error('Backend error full:', JSON.stringify(error.response?.data, null, 2));
            alert("Failed to submit purchase order for approval. Please check all fields and try again.");
        }
    }, [validateForm, selectedProductsList, orderSummary, recalculateOrderTotals, formData, roundToFourDecimals, navigate, paymentTermNumber, toNumber, unwrapId]);

    return (
        <div className="add-po-page">
             {isFormDirty() && (
                    <div className='add-po-container'>
                        <div className='action-bar'>
                            <span className='unsaved-text'>
                                <WarningIcon style={{ marginRight: '8px', verticalAlign: 'middle' }} />
                                Unsaved Purchase Order
                            </span>
                            <div className='button-group'>
                                <button className='btn discard' onClick={() => navigate(-1)}>
                                    Discard
                                </button>

                                <button className='btn save' onClick={handleSaveAsDraft}>
                                    Save as Draft
                                </button>

                                <button className='btn approve' onClick={handleSubmitForApproval}>
                                    Send for Approval
                                </button>
                            </div>
                        </div>
                    </div>
                )}

                <div className='add-po-container'>
                    <div className='po-header'>
                        <ArrowBackIcon className='back-arrow' onClick={() => navigate(-1)} />
                        <h2 className='add-po-title'>Create Purchase Order</h2>
                    </div>
                </div>

                <hr className='title-separator' />

                <div className='add-po-container'>
                    <div className='card card-select custom-card'>
                        <div className='grid-3'>
                            <div>
                                <h4>SUPPLIER<span className='required-star'>*</span></h4>
                                <Select 
                                    name="supplier"
                                    value={supplierOptions.find(option => String(option.value) === String(formData.supplier)) || null}
                                    onChange={(selectOption) => handleSelectChange(selectOption, {name: 'supplier'})}
                                    options={supplierOptions}
                                    classNamePrefix="react-select"
                                    placeholder="--Select Supplier--"
                                    isSearchable={true}
                                    isClearable={true}
                                    className={!isDraftMode && errors.supplier ? "input-error" : ""}
                                    required
                                />
                                {selectedSupplierAddress && (
                                    <p className='supplier-address'>{selectedSupplierAddress}</p>
                                )}
                            </div>

                            <div>
                                <h4>DELIVERY ADDRESS<span className='required-star'>*</span></h4>
                                <Select 
                                    name="delivery_address"
                                    value={deliveryAddressOptions.find(option => String(option.value) === String(formData.delivery_address)) || null}
                                    onChange={(selectOption) => handleSelectChange(selectOption, {name: 'delivery_address'})}
                                    options={deliveryAddressOptions}
                                    classNamePrefix="react-select"
                                    placeholder="--Select Delivery Address--"
                                    className={!isDraftMode && errors.delivery_address ? "input-error":""}
                                    isSearchable={true}
                                    isClearable={true}
                                    required
                                />
                                {selectedAddressDelivery && (
                                    <p className='supplier-address'>{selectedAddressDelivery}</p>
                                )}
                    
                                <label htmlFor="delivery_conditions" className="textarea-label"><strong>Delivery Conditions<span className='required-star'>*</span></strong></label>
                                <textarea
                                    id="delivery_conditions"
                                    name="delivery_conditions"
                                    value={formData.delivery_conditions}
                                    onChange={handleChange}
                                    className={`input textarea textarea_del_cond ${
                                        !isDraftMode && errors.delivery_conditions ? "input-error" : ""
                                    }`}
                                    placeholder="Specify delivery conditions here..."
                                    required
                                />
                            </div>

                            <div>
                                <h4>PROJECT CODE<span className='required-star'>*</span></h4>
                                <Select 
                                    name="project_code"
                                    value={projectCodeOptions.find(option => String(option.value) === String(formData.project_code)) || null}
                                    onChange={(selectOption) => handleSelectChange(selectOption, {name: 'project_code'})}
                                    options={projectCodeOptions}
                                    classNamePrefix="react-select"
                                    className={!isDraftMode && errors.project_code ? "input-error":""}
                                    placeholder="--Select Project Code--"
                                    isSearchable={true}
                                    isClearable={true}
                                    required
                                />
                                <h4 style={{marginTop: '16px'}}>SUB-PROJECT CODE</h4>
                                <Select 
                                    name='subproject_code'
                                    value={subprojectCodeOptions.find(option => String(option.value) === String(formData.subproject_code)) || null}
                                    onChange={(selectOption) =>  handleSelectChange(selectOption, {name: 'subproject_code'})}
                                    options={subprojectCodeOptions}
                                    classNamePrefix="react-select"
                                    placeholder={isSubprojectCodeDisabled ? "--Select Project Code first--" : "--Select Sub-project Code--"}
                                    isSearchable={true}
                                    isClearable={true}
                                    isDisabled={isSubprojectCodeDisabled}
                                />
                            </div>
                        </div>
                    </div>

                    <div className='card'>
                        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '20px' }}>
                            <div>
                                <h4>SHIPMENT DETAILS</h4>
                                <label>Arrival Deadline<span className='required-star'>*</span></label>
                                <div className="datepicker-container">
                                    <DatePicker
                                        selected={arrivalDeadline}
                                        onChange={handleDateChange}
                                        dateFormat="dd.MM.yyyy"
                                        placeholderText="dd.mm.yyyy"
                                        className={`datepicker-input ${!isDraftMode && errors.arrival_deadline ? "input-error" : ""}`}
                                        required
                                        wrapperClassName="datepicker-wrapper"
                                    />
                                    <span className="calendar-icon">
                                        <svg
                                            xmlns="http://www.w3.org/2000/svg"
                                            width="16"
                                            height="16"
                                            viewBox="0 0 24 24"
                                            fill="none"
                                            stroke="currentColor"
                                            strokeWidth="2"
                                            strokeLinecap="round"
                                            strokeLinejoin="round"
                                        >
                                            <rect x="3" y="4" width="18" height="18" rx="2" ry="2" />
                                            <line x1="16" y1="2" x2="16" y2="6" />
                                            <line x1="8" y1="2" x2="8" y2="6" />
                                            <line x1="3" y1="10" x2="21" y2="10" />
                                        </svg>
                                    </span>
                                </div>
                            </div>
                           <div>
                                <h4>PAYMENT</h4>
                                <label>Payment Term<span className='required-star'>*</span></label>
                                <div className="payment-wrapper">
                                    <input
                                        type="text"
                                        name="payment_term"
                                        value={paymentTermNumber || ""}
                                        onChange={handlePaymentTermChange}
                                        placeholder="Enter number of days..."
                                        className={`payment-input ${!isDraftMode && errors.payment_term ? "input-error" : ""}`}
                                    />
                                    {paymentTermNumber && (
                                        <span className="days-text">
                                            days
                                        </span>
                                    )}
                                </div>
                                {!isDraftMode && errors.payment_term && <p className="error-message">{errors.payment_term}</p>}
                            </div>
                            <div>
                                <h4>CURRENCY</h4>
                                <label>Select Currency<span className='required-star'>*</span></label>
                                <select 
                                   name="currency" 
                                   value={formData.currency} 
                                   onChange={handleChange}
                                   className={!isDraftMode && errors.currency ? "input-error" : ""}
                                >
                                    <option value="">--Select Currency--</option>
                                    {currency.map((currencies) => (
                                        <option key={currencies.id} value={currencies.id}>
                                            {currencies.name}
                                        </option>
                                    ))}
                                </select>
                            </div>
                            <div>
                                <h4>EXCHANGE RATE</h4>
                                <label>Select Exchange Rate{(!(systemCurrencyId === parseInt(formData.currency)) && formData.currency) && <span className='required-star'>*</span>}</label>
                                {formData.currency ? (
                                    (() => {
                                        const currencyId = Number(formData.currency);
                                        const exchangeOpts = exchangeOptionsByCurrencyId.get(currencyId) || [];
                                        const isSystemCurrency = systemCurrencyId === currencyId;
                                        
                                        if (isSystemCurrency) {
                                            return (
                                                <input
                                                    type="text"
                                                    value="Not needed (system currency)"
                                                    disabled
                                                    className="input-disabled"
                                                />
                                            );
                                        }
                                        
                                        if (exchangeOpts.length === 0) {
                                            return (
                                                <input
                                                    type="text"
                                                    value="No exchange rates available"
                                                    disabled
                                                    className="input-disabled"
                                                />
                                            );
                                        }
                                        
                                        return (
                                            <select
                                                name="exchange_rate"
                                                value={formData.exchange_rate || ""}
                                                onChange={(e) => {
                                                    setFormData(prev => ({
                                                        ...prev,
                                                        exchange_rate: e.target.value
                                                    }));
                                                    if (errors.exchange_rate) {
                                                        setErrors(prev => ({ ...prev, exchange_rate: "" }));
                                                    }
                                                }}
                                                className={!isDraftMode && errors.exchange_rate ? "input-error" : ""}
                                                required={!isSystemCurrency}
                                            >
                                                <option value="">--Select Exchange Rate--</option>
                                                {exchangeOpts.map(option => (
                                                    <option key={option.value} value={option.value}>
                                                        {option.label}
                                                    </option>
                                                ))}
                                            </select>
                                        );
                                    })()
                                ) : (
                                    <select disabled className="input-disabled">
                                        <option>--Select currency first--</option>
                                    </select>
                                )}
                                {!isDraftMode && errors.exchange_rate && <p className="error-message">{errors.exchange_rate}</p>}
                            </div>
                        </div>
                    </div>

                    <div className='card'>
                        <h4>ADD PRODUCTS</h4>
                        <div className='products'>
                            <input
                                type='text'
                                placeholder='Search products'
                                value={searchTerm}
                                onChange={(e) => setSearchTerm(e.target.value)}
                                autoComplete="off"
                                className="search-input"
                            />
                            <SearchIcon className="search-icon" />
                            <button onClick={() => setShowModal(true)}>Browse</button>

                            {searchTerm && (
                                <ul className='dropdown-list'>
                                    {filteredProducts.length > 0 ? (
                                        filteredProducts.map(prod => (
                                            <li
                                                key={prod.id}
                                                onClick={() => handleProductSelect(prod)}
                                                className="dropdown-item"
                                                onMouseDown={e => e.preventDefault}
                                            >
                                                {prod.title}
                                            </li>
                                        ))
                                    ) : (
                                        <li className='dropdown-item no-result'>No available products</li>
                                    )}
                                </ul>
                            )}
                        </div>
                        {selectedProductsList.length > 0 && (
                            <div className="table-responsive">
                                <table className='product-table'>
                                    <thead>
                                        <tr>
                                            <th>Products</th>
                                            <th>Quantity<span className='required-star'>*</span></th>
                                            <th>UM<span className='required-star'>*</span></th>
                                            <th>VAT (%)<span className='required-star'>*</span></th>
                                            <th>Price per unit<span className='required-star'>*</span></th>
                                            <th>Total</th>
                                            <th>System Total ({getSystemCurrencyLabel()})</th>
                                            <th>Arrival Deadline<span className='required-star'>*</span></th>
                                            <th></th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {selectedProductsList.map((prod, index) => (
                                            <tr key={index}>
                                                <td>{prod.title}</td>
                                                <td>
                                                    <NumericFormat
                                                        value={prod.quantity}
                                                        onValueChange={({ floatValue }) =>
                                                            handleProductLineChange(index, "quantity", floatValue ?? 0)
                                                        }
                                                        thousandSeparator="."
                                                        decimalSeparator=","
                                                        decimalScale={4}
                                                        fixedDecimalScale={false}
                                                        allowNegative={false}
                                                        inputMode="decimal"
                                                        className={!isDraftMode && errors[`quantity_${index}`] ? "input-error":""}
                                                    />
                                                </td>
                                                <td>
                                                    <input
                                                        type="text"
                                                        value={prod.um}
                                                        onChange={e => handleProductLineChange(index, "um", e.target.value)}
                                                        className={!isDraftMode && errors[`um_${index}`] ? "input-error":""}
                                                    />
                                                </td>
                                                <td>
                                                    <NumericFormat
                                                        value={prod.vat}
                                                        onValueChange={({ floatValue }) =>
                                                            handleProductLineChange(index, "vat", floatValue ?? 0)
                                                        }
                                                        thousandSeparator={false}
                                                        decimalSeparator=","
                                                        decimalScale={4}
                                                        allowNegative={false}
                                                        fixedDecimalScale={false}
                                                        inputMode="decimal"
                                                        className={!isDraftMode && errors[`vat_${index}`] ? "input-error" : ""}
                                                    />
                                                </td>
                                                <td>
                                                    <NumericFormat
                                                        value={prod.cost}
                                                        onValueChange={({ floatValue }) =>
                                                            handleProductLineChange(index, "cost", floatValue ?? 0)
                                                        }
                                                        thousandSeparator="."
                                                        decimalSeparator=","
                                                        decimalScale={4}
                                                        fixedDecimalScale={false}
                                                        allowNegative={false}
                                                        inputMode="decimal"
                                                        className={!isDraftMode && errors[`cost_${index}`] ? "input-error" : ""}
                                                    />
                                                </td>
                                                <td>
                                                    <NumericFormat
                                                        value={Number(prod.total) || 0}
                                                        thousandSeparator="."
                                                        decimalSeparator=","
                                                        decimalScale={2}       
                                                        fixedDecimalScale={true} 
                                                        inputMode="decimal"
                                                        readOnly
                                                    />
                                                </td>
                                                <td>
                                                    <NumericFormat
                                                        value={Number(prod.total_system_currency) || 0}
                                                        thousandSeparator="."
                                                        decimalSeparator=","
                                                        decimalScale={2}
                                                        fixedDecimalScale={true}
                                                        inputMode='decimal'
                                                        readOnly
                                                    />
                                                </td>
                                                <td>
                                                    <div className="datepicker-field">
                                                        <DatePicker
                                                            selected={prod.arrival_deadline ? new Date(prod.arrival_deadline) : null}
                                                            onChange={(date) => {
                                                                if (!date) {
                                                                    handleProductLineChange(index, "arrival_deadline", "");
                                                                    return;
                                                                }

                                                                const year = date.getFullYear();
                                                                const month = String(date.getMonth() + 1).padStart(2, "0");
                                                                const day = String(date.getDate()).padStart(2, "0");

                                                                const formattedDate = `${year}-${month}-${day}`;

                                                                handleProductLineChange(index, "arrival_deadline", formattedDate);
                                                            }}
                                                            dateFormat="dd.MM.yyyy"
                                                            placeholderText="dd.mm.yyyy"
                                                            className={`arrival_date ${!isDraftMode && errors[`arrival_${index}`] ? "input-error" : ""}`}
                                                            wrapperClassName="datepicker-wrapper"
                                                            popperProps={{ strategy: "fixed" }}
                                                            popperContainer={({ children }) => (
                                                                <div style={{ position: "fixed", zIndex: 99999 }}>
                                                                    {children}
                                                                </div>
                                                            )}
                                                        />
                                                        <span className="calendar-icon">
                                                            <svg
                                                                xmlns="http://www.w3.org/2000/svg"
                                                                width="16"
                                                                height="16"
                                                                viewBox="0 0 24 24"
                                                                fill="none"
                                                                stroke="currentColor"
                                                                strokeWidth="2"
                                                                strokeLinecap="round"
                                                                strokeLinejoin="round"
                                                            >
                                                                <rect x="3" y="4" width="18" height="18" rx="2" ry="2" />
                                                                <line x1="16" y1="2" x2="16" y2="6" />
                                                                <line x1="8" y1="2" x2="8" y2="6" />
                                                                <line x1="3" y1="10" x2="21" y2="10" />
                                                            </svg>
                                                        </span>
                                                    </div>
                                                </td>
                                                <td>
                                                    <button onClick={() => handleAskDeleteProduct(index)} aria-label="delete" size="small">
                                                        <DeleteIcon style={{ color: 'red' }} />
                                                    </button>
                                                </td>
                                            </tr>
                                        ))}
                                        
                                        <tr>
                                            <td style={{ verticalAlign: 'middle' }}>
                                                <strong style={{ 
                                                    fontSize: '16px', 
                                                    fontWeight: 600, 
                                                    borderLeft: '3px solid #2e5bee', 
                                                    paddingLeft: '12px',
                                                    display: 'inline-block',
                                                    lineHeight: '42px'
                                                }}>
                                                    Taxes
                                                </strong>
                                            </td>
                                            <td colSpan={2}>
                                                <select
                                                    name="tax"
                                                    value={formData.tax}
                                                    onChange={handleChange}
                                                    style={{ width: '100%', margin: 0 }}
                                                >
                                                    <option value="">--Select taxes--</option>
                                                    {taxes.map((tax) => (
                                                        <option key={tax.id} value={tax.id}>
                                                            {tax.name}
                                                        </option>
                                                    ))}
                                                </select>
                                            </td>
                                            <td>
                                                <NumericFormat
                                                    value={formData.vat_value_tax}
                                                    onValueChange={({ value }) =>
                                                        handleChange({ target: { name: "vat_value_tax", value: value || "0" } })
                                                    }
                                                    thousandSeparator={false}
                                                    decimalSeparator=","
                                                    decimalScale={4}
                                                    allowNegative={false}
                                                    fixedDecimalScale={false}
                                                    inputMode="decimal"
                                                    readOnly={!formData.tax}
                                                    style={{ margin: 0 }}
                                                />
                                            </td>
                                            <td>
                                                <NumericFormat
                                                    value={formData.tax_value}
                                                    onValueChange={({ value }) =>
                                                        handleChange({ target: { name: "tax_value", value: value || "0" } })
                                                    }
                                                    thousandSeparator={false}
                                                    decimalSeparator=","
                                                    decimalScale={4}
                                                    allowNegative={false}
                                                    fixedDecimalScale={false}
                                                    inputMode="decimal"
                                                    readOnly={!formData.tax}
                                                    style={{ margin: 0 }}
                                                />
                                            </td>
                                            <td>
                                                <NumericFormat
                                                    value={Number(orderSummary?.total_delivery || 0)}
                                                    thousandSeparator="."
                                                    decimalSeparator=","
                                                    decimalScale={2}
                                                    fixedDecimalScale={true}
                                                    displayType="input"
                                                    readOnly
                                                    className="input"
                                                    style={{ margin: 0 }}
                                                />
                                            </td>
                                            <td>
                                                <NumericFormat
                                                    value={Number(orderSummary?.total_delivery_system_currency || 0)}
                                                    thousandSeparator="."
                                                    decimalSeparator=','
                                                    decimalScale={2}
                                                    fixedDecimalScale={true}
                                                    displayType='input'
                                                    className='input'
                                                    readOnly
                                                    style={{margin: 0}}
                                                />
                                            </td>
                                            <td></td>
                                        </tr>
                                        <tr>
                                            <td style={{ fontSize: '12px' }} colSpan={8}>
                                                {selectedProductsList.length} variants on purchase order
                                            </td>
                                        </tr>
                                    </tbody>
                                </table>
                            </div>
                        )}
                    </div>

                    <div className='grid-2'>
                        <div className='card'>
                            <h4>ADDITIONAL DETAILS</h4>
                            <h4>Note to Supplier</h4>
                            <textarea
                                name="note_to_supplier"
                                value={formData.note_to_supplier || ''}
                                onChange={handleChange}
                                maxLength={5000}
                                placeholder='Write a note...'
                            />
                            <div className="char-count">{(formData.note_to_supplier || '').length}/5000</div>
                        </div>

                        {/* COST SUMMARY CARD */}
                        <div className='card'>
                            <h4>COST SUMMARY</h4>
                            <hr/>
                            {isCalculating ? (
                                <div style={{ textAlign: 'center', padding: '40px' }}>
                                    Calculating...
                                </div>
                            ) : (
                                (() => {
                                    const summary = orderSummary || {
                                        // Valori în moneda originală
                                        total_without_vat: 0,
                                        total_vat: 0,
                                        delivery_tax: 0,
                                        delivery_vat: 0,
                                        total_delivery: 0,
                                        total: 0,
                                        // Valori în moneda sistemului
                                        total_without_vat_system: 0,
                                        total_vat_system: 0,
                                        delivery_tax_system: 0,
                                        total_delivery_system_currency: 0,
                                        total_system_currency: 0
                                    };
                                    const currencyLabel = getSelectedCurrencyLabel();
                                    const systemCurrencyLabel = getSystemCurrencyLabel();

                                    const formatValue = (value) => {
                                        return Number(value).toLocaleString('ro-RO', {
                                            minimumFractionDigits: 2,
                                            maximumFractionDigits: 2
                                        });
                                    };

                                    return (
                                        <div className="summary-container">
                                        <table className="summary-table">
                                            <thead>
                                            <tr className="table-header">
                                                <th className="cell-left"></th>
                                                <th className="cell-right header-currency">Total Currency</th>
                                                <th className="cell-right header-system">System Total ({systemCurrencyLabel})</th>
                                            </tr>
                                            </thead>
                                            <tbody>
                                            {/* Total without VAT */}
                                            <tr className="table-row">
                                                <td className="cell-left label">Total without VAT</td>
                                                <td className="cell-right with-arrow">
                                                {formatValue(summary.total_without_vat)} {currencyLabel}
                                                </td>
                                                <td className="cell-right system-value">
                                                {formatValue(summary.total_without_vat_system)} {systemCurrencyLabel}
                                                </td>
                                            </tr>

                                            {/* Delivery Tax */}
                                            <tr className="table-row">
                                                <td className="cell-left label">Taxes without VAT</td>
                                                <td className="cell-right with-arrow">
                                                {formatValue(summary.delivery_tax)} {currencyLabel}
                                                </td>
                                                <td className="cell-right system-value">
                                                {formatValue(summary.delivery_tax_system)} {systemCurrencyLabel}
                                                </td>
                                            </tr>

                                            {/* VAT */}
                                            <tr className="table-row">
                                                <td className="cell-left label">VAT</td>
                                                <td className="cell-right with-arrow">
                                                {formatValue(summary.total_vat)} {currencyLabel}
                                                </td>
                                                <td className="cell-right system-value">
                                                {formatValue(summary.total_vat_system)} {systemCurrencyLabel}
                                                </td>
                                            </tr>

                                            {/* Total */}
                                            <tr className="total-row">
                                                <td className="cell-left total-label">TOTAL</td>
                                                <td className="cell-right total-value with-arrow">
                                                <strong>{formatValue(summary.total)} {currencyLabel}</strong>
                                                </td>
                                                <td className="cell-right total-system-value">
                                                <strong>{formatValue(summary.total_system_currency)} {systemCurrencyLabel}</strong>
                                                </td>
                                            </tr>
                                            </tbody>
                                        </table>
                                        </div>
                                    );
                                })()
                            )}
                        </div>
                    </div>
                </div>

            {showModal && (
                <BrowseProduct
                    onClose={() => setShowModal(false)}
                    onSelectProducts={(selectedProducts) => {
                        const newProducts = selectedProducts.map(prod => ({
                            ...prod,
                            quantity: "",
                            um: "",
                            vat: "21",
                            cost: "",
                            subtotal: 0,
                            vat_amount: 0,
                            total: 0,
                            total_system_currency: 0,
                            arrival_deadline: formData.arrival_deadline || ""
                        }));

                        setSelectedProductsList(prev => {
                            const existingIds = new Set(prev.map(p => p.id));
                            const filteredNew = newProducts.filter(p => !existingIds.has(p.id));
                            return [...prev, ...filteredNew];
                        });

                        setShowModal(false);
                    }}
                />
            )}

            {showConfirmModal && (
                <div className="confirm-overlay">
                    <div className="confirm-box">
                        <p>Are you sure you want to delete this product?</p>
                        <div className="confirm-actions">
                            <button onClick={confirmDeleteProduct} className="btn btn-danger">Yes, delete</button>
                            <button onClick={() => setShowConfirmModal(false)} className="btn btn-secondary">Cancel</button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default CreatePurchaseOrder;